/**
 * The paper that lets a mould off the premises.
 *
 * Printed and handed to the driver, signed at both ends. On customer-owned
 * tooling this is the document that proves a tool left with authorisation and
 * arrived somewhere expected - the thing you produce when a customer asks where
 * their mould went, months later.
 *
 * Portalled to <body> for the same reason the QR label is: the dialog's flex and
 * overflow ancestors collapse the printed copy to nothing, and a gate pass that
 * prints blank is worse than no gate pass, because nobody notices until the
 * driver is at the gate.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { QRCodeSVG } from 'qrcode.react';
import type { TransferDetail } from '@mouldtrack/shared';

const PRINTING_CLASS = 'printing-gate-pass';

function line(label: string, value: string | null | undefined) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '3px 0' }}>
      <span style={{ width: 150, color: '#555', fontSize: 12 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{value || '-'}</span>
    </div>
  );
}

export function GatePass({
  transfer,
  fromLabel,
  toLabel,
  companyName,
  onDone,
}: {
  transfer: TransferDetail;
  fromLabel: string;
  toLabel: string;
  companyName: string;
  onDone: () => void;
}) {
  useEffect(() => {
    document.body.classList.add(PRINTING_CLASS);
    const cleanup = () => {
      document.body.classList.remove(PRINTING_CLASS);
      onDone();
    };
    // afterprint covers both "printed" and "cancelled the print dialog"; without
    // it the class sticks and the app stays hidden behind a print-only stylesheet.
    window.addEventListener('afterprint', cleanup, { once: true });
    const t = window.setTimeout(() => window.print(), 60);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('afterprint', cleanup);
      document.body.classList.remove(PRINTING_CLASS);
    };
  }, [onDone]);

  const issued = new Date(transfer.createdAt).toLocaleString();
  const due = transfer.expectedArrivalAt
    ? new Date(transfer.expectedArrivalAt).toLocaleDateString()
    : null;

  return createPortal(
    <div id="gate-pass-sheet" style={{ padding: 28, fontFamily: 'system-ui, sans-serif', color: '#111' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{companyName}</div>
          <div style={{ fontSize: 15, marginTop: 2 }}>Gate Pass / Delivery Challan</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <QRCodeSVG value={transfer.transferNo} size={84} level="M" />
          <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
            {transfer.transferNo}
          </div>
        </div>
      </div>

      <hr style={{ margin: '14px 0', border: 0, borderTop: '2px solid #111' }} />

      {line('Transfer number', transfer.transferNo)}
      {line('Issued', issued)}
      {line('Mould ID', transfer.mould?.assetCode)}
      {line('Mould name', transfer.mould?.name)}
      {line('From', fromLabel)}
      {line('To', toLabel)}
      {line('Expected arrival', due)}

      <hr style={{ margin: '14px 0', border: 0, borderTop: '1px solid #bbb' }} />

      <p style={{ fontSize: 11, color: '#444', lineHeight: 1.5 }}>
        The tooling described above is released for movement between the locations stated. It
        remains the property of its registered owner throughout. This pass must accompany the
        consignment and be surrendered on arrival.
      </p>

      {/* Three signatures, because that is who actually touches a tool leaving a
          site: the person who released it, the person who drove it, the person
          who took it in. */}
      <div style={{ display: 'flex', gap: 24, marginTop: 42 }}>
        {['Released by', 'Transported by', 'Received by'].map((role) => (
          <div key={role} style={{ flex: 1 }}>
            <div style={{ borderTop: '1px solid #111', paddingTop: 4, fontSize: 11, color: '#444' }}>
              {role}
            </div>
            <div style={{ fontSize: 10, color: '#888', marginTop: 12 }}>Name / Signature / Date</div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}
